import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, Users, Plus, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfWeek, addDays, isSameDay } from "date-fns";
import { useAppointments } from "@/hooks/useAppointments";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function CalendarScheduling() {
  const [selectedView, setSelectedView] = useState("week");
  const [selectedProvider, setSelectedProvider] = useState("all");
  const [currentDate, setCurrentDate] = useState(new Date());
  const { appointments } = useAppointments();
  const { profile } = useAuth();

  // Get current week/month dates
  const startOfCurrentWeek = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
  
  // Determine how many days to show based on view
  let daysToShow = 1; // default for day view
  if (selectedView === "week") {
    daysToShow = 5; // weekdays
  } else if (selectedView === "month") {
    daysToShow = 35; // 5 weeks grid for month view
  }
  
  const currentDates = Array.from(
    { length: daysToShow },
    (_, i) => addDays(selectedView === "day" ? currentDate : startOfCurrentWeek, i)
  );

  const timeSlots = [
    "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
    "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
    "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"
  ];
  
  // Fetch providers from the clinic
  const { data: providers = [] } = useQuery({
    queryKey: ['clinic-providers', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return [];
      
      try {
        console.log('DEBUG: CalendarScheduling fetching providers for clinic:', profile.clinic_id);
        
        // Use RPC function instead of non-existent provider_profiles table
        const { data, error } = await supabase
          .rpc('get_providers_for_booking', { 
            clinic_uuid: profile.clinic_id 
          });
          
        console.log('DEBUG: CalendarScheduling providers RPC result:', { data, error });
        
        if (error) {
          console.error('Error fetching providers:', error);
          return [];
        }
        
        if (!data || data.length === 0) {
          console.log('DEBUG: CalendarScheduling - No providers found');
          return [];
        }
        
        return data.map((provider: any) => ({
          id: provider.id,
          name: `Dr. ${provider.first_name} ${provider.last_name}`
        }));
      } catch (err) {
        console.error('Exception in providers query:', err);
        return [];
      }
    },
    enabled: !!profile?.clinic_id
  });

  // Filter appointments for the current view and convert to the format expected by the calendar
  const filteredAppointments = appointments
    .filter(apt => {
      // Check if appointment date falls within current dates
      const aptDate = new Date(apt.date);
      return currentDates.some(date => isSameDay(aptDate, date));
    })
    .map(apt => {
      // Find which day this appointment falls on
      const aptDate = new Date(apt.date);
      const dayIndex = currentDates.findIndex(date => isSameDay(aptDate, date));
      
      return {
        ...apt,
        day: dayIndex + 1, // Convert to 1-based index for compatibility
      };
    });

  const getAppointmentStyle = (appointment: any) => {
    const colors = {
      "Consultation": "bg-blue-100 border-blue-300 text-blue-800",
      "Follow-up": "bg-green-100 border-green-300 text-green-800",
      "Procedure": "bg-purple-100 border-purple-300 text-purple-800",
      "Check-up": "bg-orange-100 border-orange-300 text-orange-800",
    };
    return colors[appointment.type as keyof typeof colors] || "bg-gray-100 border-gray-300 text-gray-800";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendar & Scheduling</h1>
          <p className="text-gray-600">Manage appointments and view clinic schedule.</p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(Date.now() - 86400000))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(Date.now() + 86400000))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Week of {format(startOfCurrentWeek, "MMM d, yyyy")}</span>
              </div>
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  {providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                variant={selectedView === "day" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedView("day")}
              >
                Day
              </Button>
              <Button
                variant={selectedView === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedView("week")}
              >
                Week
              </Button>
              <Button
                variant={selectedView === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedView("month")}
              >
                Month
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            {selectedView === "day" ? "Daily Schedule" : 
             selectedView === "week" ? "Weekly Schedule" : "Monthly Schedule"}
          </CardTitle>
          <CardDescription>Drag and drop to reschedule appointments</CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          {selectedView !== "month" ? (
            <>
              <div className="grid grid-cols-6 gap-2 mb-2">
                <div className="text-xs font-medium text-gray-500">Time</div>
                {currentDates.slice(0, selectedView === "day" ? 1 : 5).map((date, index) => (
                  <div key={index} className="text-xs font-medium text-center text-gray-700 p-1">
                    {format(date, "EEE")}
                    <div className="text-xs text-gray-500">{format(date, "MMM d")}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-6 gap-2 max-h-[500px] overflow-y-auto">
                {timeSlots.map((time) => (
                  <div key={time} className="contents">
                    <div className="text-xs text-gray-500 py-1 border-t border-gray-100">
                      {time}
                    </div>
                    {currentDates.slice(0, selectedView === "day" ? 1 : 5).map((_, dayIndex) => {
                      const dayAppointments = filteredAppointments.filter(
                        (apt) => apt.day === dayIndex + 1 && apt.time === time
                      );
                      
                      return (
                        <div
                          key={`${time}-${dayIndex}`}
                          className="min-h-[60px] p-1 border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          {dayAppointments.map((appointment) => (
                            <div
                              key={appointment.id}
                              className={`p-1 rounded-lg border-l-4 transition-shadow hover:shadow-md ${getAppointmentStyle(appointment)}`}
                            >
                              <div className="text-xs font-medium truncate">{appointment.patient}</div>
                              <div className="text-xs text-gray-600 truncate">
                                {appointment.reason || appointment.type}
                              </div>
                              <div className="text-xs text-gray-600 truncate">{appointment.provider}</div>
                              <div className="flex items-center gap-1 text-xs">
                                <Clock className="h-3 w-3" />
                                <span>{appointment.duration}min</span>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {appointment.duration}min
                                </Badge>
                                {appointment.status && (
                                  <Badge 
                                    variant={appointment.status === "confirmed" ? "default" : 
                                           appointment.status === "cancelled" ? "destructive" : "outline"}
                                    className={`text-xs ${appointment.status === "confirmed" ? "bg-green-100 text-green-800 hover:bg-green-200" : ""}`}
                                  >
                                    {appointment.status}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Month view - calendar grid
            <div className="mt-2">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <div key={day} className="text-xs font-medium text-center text-gray-700 p-1">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid - 5 weeks, 7 days each */}
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 35 }, (_, i) => {
                  const date = addDays(startOfCurrentWeek, i);
                  const dayAppointments = filteredAppointments.filter(
                    (apt) => {
                      const aptDate = new Date(apt.date);
                      return isSameDay(aptDate, date);
                    }
                  );
                  
                  return (
                    <div
                      key={i}
                      className={`min-h-[80px] p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer transition-colors ${
                        isSameDay(date, new Date()) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="text-xs font-medium mb-1">{format(date, 'd')}</div>
                      
                      {/* Show up to 3 appointments with count of additional ones */}
                      {dayAppointments.slice(0, 3).map((appointment) => (
                        <div 
                          key={appointment.id}
                          className={`p-1 mb-1 rounded text-xs truncate border-l-2 ${getAppointmentStyle(appointment)}`}
                        >
                          {appointment.time} - {appointment.patient}
                        </div>
                      ))}
                      
                      {dayAppointments.length > 3 && (
                        <div className="text-xs text-blue-600 font-medium mt-1">
                          +{dayAppointments.length - 3} more
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{filteredAppointments.length}</p>
                <p className="text-sm text-gray-600">
                  {selectedView === "day" ? "Today's" : 
                   selectedView === "week" ? "This Week's" : "This Month's"} Appointments
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
